import { PluginListenerHandle } from '@capacitor/core';
import { isPlatform } from '@ionic/react';
import { isEqual, isNil } from 'lodash';
import { useObservable, useSubscription } from 'observable-hooks';
import { coalesceAsync } from 'promise-coalesce';
import {
  MutableRefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Subject, catchError, filter, firstValueFrom, of, timeout } from 'rxjs';
import { WifiExtension } from '../../plugins/wifi.plugin';
import { runInBackground } from '../../util/async';
import { logger } from '../../util/logger';

export enum WifiExtensionEventType {
  WIFI_INFO_DID_CHANGE = 'wifiInfoDidChange',
}

export interface WifiExtensionWifiInfoDidChangeEvent {
  // iOS `nil` values are returned to us as `null`
  wifi: WifiExtensionWifiInfo | null;
}

export interface WifiExtensionWifiInfo {
  // iOS `nil` values are returned to us as `null`
  ssid: string | null;
  ipAddress: string | null;
  gateway: string | null;
}

export enum WifiSubscriptionEventType {
  /**
   * We are connected to a new wifi network.
   */
  CONNECTED = 'connected',
  /**
   * We are connecting to a new wifi network.
   */
  CONNECTING = 'connecting',
  /**
   * We disconnected from this old wifi network.
   */
  DISCONNECTED = 'disconnected',
  /**
   * We are disconnecting from this old wifi network.
   */
  DISCONNECTING = 'disconnecting',
  /**
   * We failed to connect to a new wifi network.
   */
  FAILED_CONNECTING = 'failed_connecting',
}

export interface WifiSubscriptionEvent {
  type: WifiSubscriptionEventType;
  /**
   * When the type is 'connected' then this will be the new wifi info.
   * When the type is 'disconnected' then this will be the old wifi info.
   */
  wifiInfo: WifiInfo;
}

export interface WifiInfo {
  ssid?: string;
  ipAddress?: string;
  gateway?: string;
}

// iOS `nil` values are returned to us as `null`
// but for convention we want to standardize on `undefined`.
const convertWifiExtensionInfoToJS = (
  wifiInfo: WifiExtensionWifiInfo | null
): WifiInfo | undefined => {
  if (!wifiInfo) {
    return;
  }

  return {
    gateway: wifiInfo.gateway ?? undefined,
    ipAddress: wifiInfo.ipAddress ?? undefined,
    ssid: wifiInfo.ssid ?? undefined,
  };
};

interface IsConnectedToWifiOptions {
  /**
   * Check if we are connected to this specific wifi network.
   */
  ssid?: string;
  ssidRegex?: RegExp;
}

export const isConnectedToWifi = (
  wifiInfo: WifiInfo,
  options?: IsConnectedToWifiOptions
): boolean => {
  const hasGateway = !isNil(wifiInfo?.gateway);
  const hasIpAddress = !isNil(wifiInfo?.ipAddress);

  let hasSameAddressSpace = false;

  if (wifiInfo.gateway && wifiInfo.ipAddress) {
    const gatewayAddressSpace = wifiInfo.gateway.split('.')[0];
    const ipAddressAddressSpace = wifiInfo.ipAddress.split('.')[0];
    hasSameAddressSpace = gatewayAddressSpace === ipAddressAddressSpace;
  }

  const isConnected = hasGateway && hasIpAddress && hasSameAddressSpace;

  if (options?.ssid) {
    const hasSameSsid = wifiInfo.ssid === options.ssid;
    return isConnected && hasSameSsid;
  }

  if (options?.ssidRegex) {
    const hasMatchingSsid = options.ssidRegex.test(wifiInfo.ssid ?? '');
    return isConnected && hasMatchingSsid;
  }

  return isConnected;
};

export const canPlatformUseWifi = isPlatform('capacitor') && isPlatform('ios');

/**
 * Subscribes to wifi changes.
 *
 * Returns an observable that emits wifi subscription events, specifically
 * when we connect or disconnect from a wifi network.
 *
 * Also returns a ref that holds the current wifi info, which is set
 * and cleared when we connect or disconnect from a wifi network.
 *
 * You can use either the observable or the ref to get the current wifi info.
 * Some people want to know when the wifi changes, others just want to know
 * what it is in the moment.
 */
const useWifiSubscription = (): [
  Subject<WifiSubscriptionEvent>,
  MutableRefObject<WifiInfo | undefined>,
] => {
  // If connected to wifi then this will be the wifi info.
  // If not connected to wifi then this will be `undefined`.
  const wifiInfoRef = useRef<WifiInfo | undefined>(undefined);

  const wifiInfoSubject$ = useObservable(() => {
    return new Subject<WifiSubscriptionEvent>();
  });

  // Record that we are connected to this wifi then notify subscribers.
  const handleConnect = useCallback(
    (newWifiInfo: WifiInfo): void => {
      wifiInfoRef.current = newWifiInfo;

      wifiInfoSubject$.next({
        type: WifiSubscriptionEventType.CONNECTED,
        wifiInfo: newWifiInfo,
      });
    },
    [wifiInfoSubject$]
  );

  // Record that we are disconnected from this wifi then notify subscribers.
  const handleDisconnect = useCallback(
    (oldWifiInfo: WifiInfo): void => {
      wifiInfoRef.current = undefined;

      wifiInfoSubject$.next({
        type: WifiSubscriptionEventType.DISCONNECTED,
        wifiInfo: oldWifiInfo,
      });
    },
    [wifiInfoSubject$]
  );

  useEffect(() => {
    if (!canPlatformUseWifi) {
      logger.info('Wifi is not supported on this platform.');
      return;
    }

    let pluginListenerHandle: PluginListenerHandle | undefined;

    runInBackground(async () => {
      // Subscribe to wifi info changes.
      pluginListenerHandle = await WifiExtension.addListener(
        WifiExtensionEventType.WIFI_INFO_DID_CHANGE,
        (event: WifiExtensionWifiInfoDidChangeEvent) => {
          logger.info('Wifi info update', event);

          const wifiInfo = convertWifiExtensionInfoToJS(event.wifi);

          // The wifi extension says we don't have any wifi info.
          if (!wifiInfo) {
            // If we were previouslly connected to something then emit
            // that we are no longer connected to it.
            if (wifiInfoRef.current) {
              handleDisconnect(wifiInfoRef.current);
            }
            return;
          }

          // The wifi extension says we have wifi info.
          if (isConnectedToWifi(wifiInfo)) {
            // If this is new information to us.
            if (!isEqual(wifiInfo, wifiInfoRef.current)) {
              // And if we were previously connected to something then emit
              // that we are no longer connected to it.
              if (wifiInfoRef.current) {
                handleDisconnect(wifiInfoRef.current);
              }
              // Emit that we are now connected to this new wifi info.
              handleConnect(wifiInfo);
            }
          }
        }
      );

      // Get the current wifi info, if any.
      const wifiInfo = convertWifiExtensionInfoToJS(
        await WifiExtension.getWifiInfo()
      );
      if (wifiInfo && isConnectedToWifi(wifiInfo)) {
        handleConnect(wifiInfo);
      }
    });

    return () => {
      runInBackground(async () => {
        // Unsubscribe from wifi info changes.
        await pluginListenerHandle?.remove();
      });
    };
  }, [handleConnect, handleDisconnect]);

  return [wifiInfoSubject$, wifiInfoRef];
};

interface ConnectToWifiOptions {
  ssid: string;
  passphrase: string;
  connectionTimeout?: number;
}

/**
 * Imperative function to connect to a wifi network.
 * If successful then resolves with the wifi info.
 * If unsuccessful then resolves with `undefined`.
 */
export type ConnectToWifi = (
  options: ConnectToWifiOptions
) => Promise<WifiInfo | undefined>;

interface DisconnectFromWifiOptions {
  ssid: string;
}

/**
 * Imperative function to disconnect from a wifi network.
 * If not connected to, or disconnect from, the SSID then resolves with `true`.
 * If time out trying to disconnect then resolves with `false`.
 */
export type DisconnectFromWifi = (
  options: DisconnectFromWifiOptions
) => Promise<boolean>;

interface WifiContextValue {
  connectToWifi: ConnectToWifi;
  disconnectFromWifi: DisconnectFromWifi;
  wifiSubscription$: Subject<WifiSubscriptionEvent>;
  wifiInfoRef: MutableRefObject<WifiInfo | undefined>;
}

const WifiContext = createContext<WifiContextValue>({} as WifiContextValue);

interface WifiProviderProps {
  children: React.ReactNode;
}

export const WifiProvider = (props: WifiProviderProps) => {
  const { children } = props;
  const [wifiSubscription$, wifiInfoRef] = useWifiSubscription();

  const connectToWifi = useCallback(
    async (options: ConnectToWifiOptions): Promise<WifiInfo | undefined> => {
      // At most one attempt to connect to wifi at a time.
      // We use `coalesceAsync` to ensure that only one attempt is made
      // while ensuring all concurrent requests resolve with same result.
      return coalesceAsync(
        'connect-to-wifi-exclusive-lock',
        async (): Promise<WifiInfo | undefined> => {
          if (!canPlatformUseWifi) {
            logger.info(
              'Cannot connect to wifi. Wifi is not supported on this platform.'
            );
            return;
          }

          // If we are already connected to this wifi then return early.
          if (
            wifiInfoRef.current &&
            isConnectedToWifi(wifiInfoRef.current, { ssid: options.ssid })
          ) {
            logger.info(
              'Cannot connect. Already connected to wifi',
              wifiInfoRef.current
            );

            return wifiInfoRef.current;
          }

          wifiSubscription$.next({
            type: WifiSubscriptionEventType.CONNECTING,
            wifiInfo: {
              ssid: options.ssid,
            },
          });

          const connectToWifiEvent$ = wifiSubscription$.pipe(
            filter((event) => {
              const { type, wifiInfo } = event;

              if (type !== WifiSubscriptionEventType.CONNECTED) {
                return false;
              }

              const isConnectedToSSID = isConnectedToWifi(wifiInfo, {
                ssid: options.ssid,
              });

              if (!isConnectedToSSID) {
                return false;
              }

              return true;
            }),
            // TODO: This should start after we await the connect function
            timeout({
              first: options.connectionTimeout ?? 30_000,
            }),
            catchError((error) => {
              logger.error('Wifi connect timeout', { error });
              wifiSubscription$.next({
                type: WifiSubscriptionEventType.FAILED_CONNECTING,
                wifiInfo: {
                  ssid: options.ssid,
                },
              });
              return of(undefined);
            })
          );

          // Attempt to connect to the wifi network.
          logger.info('Connecting to wifi', { options });
          try {
            await WifiExtension.connect(options);
          } catch (error) {
            logger.error('Failed to connect to wifi', { error });
            wifiSubscription$.next({
              type: WifiSubscriptionEventType.FAILED_CONNECTING,
              wifiInfo: {
                ssid: options.ssid,
              },
            });
            return;
          }

          const wifiEvent = await firstValueFrom(connectToWifiEvent$);
          return wifiEvent?.wifiInfo;
        }
      );
    },
    [wifiSubscription$, wifiInfoRef]
  );

  const disconnectFromWifi = useCallback(
    async (options: DisconnectFromWifiOptions): Promise<boolean> => {
      // At most one attempt to disconnect from wifi at a time.
      // We use `coalesceAsync` to ensure that only one attempt is made
      // while ensuring all concurrent requests resolve with same result.
      return coalesceAsync(
        'disconnect-from-wifi-exclusive-lock',
        async (): Promise<boolean> => {
          if (!canPlatformUseWifi) {
            logger.info(
              'Cannot disconnect from wifi. Wifi is not supported on this platform.'
            );
            return false;
          }

          // If we are already disconnected from this wifi then return early.
          if (
            !wifiInfoRef.current ||
            !isConnectedToWifi(wifiInfoRef.current, { ssid: options.ssid })
          ) {
            logger.info('Cannot disconnect. Already disconnected from wifi');
            return true;
          }

          wifiSubscription$.next({
            type: WifiSubscriptionEventType.DISCONNECTING,
            wifiInfo: {
              ssid: options.ssid,
            },
          });

          const disconnectFromWifiEvent$ = wifiSubscription$.pipe(
            filter((event) => {
              const { type, wifiInfo } = event;

              // Only interessted in disconnect events.
              if (type !== WifiSubscriptionEventType.DISCONNECTED) {
                return false;
              }

              // Only interested in disconnect events for this ssid.
              if (wifiInfo.ssid !== options.ssid) {
                return false;
              }

              // We disconnected from the desired wifi network.
              return true;
            }),
            timeout({
              first: 10_000,
            }),
            catchError((error) => {
              logger.error('Failed to disconnect. Wifi disconnect timeout', {
                error,
              });
              return of(undefined);
            })
          );

          // Attempt to disconnect from the wifi network.
          logger.info('Disconnecting from wifi', { options });
          await WifiExtension.disconnect();

          const wifiEvent = await firstValueFrom(disconnectFromWifiEvent$);
          return !isNil(wifiEvent);
        }
      );
    },
    [wifiSubscription$, wifiInfoRef]
  );

  const wifiContextValue = useMemo(() => {
    return {
      connectToWifi,
      disconnectFromWifi,
      wifiSubscription$,
      wifiInfoRef,
    };
  }, [connectToWifi, disconnectFromWifi, wifiSubscription$, wifiInfoRef]);

  return (
    <WifiContext.Provider value={wifiContextValue}>
      {children}
    </WifiContext.Provider>
  );
};

export const useWifiContext = (): WifiContextValue => {
  return useContext(WifiContext);
};

/**
 * Same as `useWifiSubscription` but instead of being given an
 * observable and a ref, it returns a reactive state value.
 * Use this if you want to trigger a re-render when the wifi changes.
 */
export const useWifiSubscriptionWithState = (): [WifiInfo | undefined] => {
  const { wifiSubscription$, wifiInfoRef } = useWifiContext();
  const [wifiInfo, setWifiInfo] = useState<WifiInfo | undefined>(
    wifiInfoRef.current
  );

  useSubscription(wifiSubscription$, (event: WifiSubscriptionEvent) => {
    switch (event.type) {
      case WifiSubscriptionEventType.CONNECTED:
        setWifiInfo(event.wifiInfo);
        break;
      case WifiSubscriptionEventType.DISCONNECTED:
        setWifiInfo(undefined);
        break;
    }
  });

  return [wifiInfo];
};

type UnsubscribeFromWifiEvent = () => void;
type OnWifiEvents = (
  events: WifiSubscriptionEventType[],
  callback: () => void
) => UnsubscribeFromWifiEvent;

export const useOnWifiEvents = (): OnWifiEvents => {
  const { wifiSubscription$ } = useWifiContext();
  const unsubscribeRef = useRef<UnsubscribeFromWifiEvent | undefined>();

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  const onWifiEvents: OnWifiEvents = useCallback(
    (
      subscriptionEvents: WifiSubscriptionEventType[],
      callback: () => void
    ): UnsubscribeFromWifiEvent => {
      const subscription = wifiSubscription$.subscribe(
        (event: WifiSubscriptionEvent) => {
          if (subscriptionEvents.includes(event.type)) {
            callback();
          }
        }
      );

      unsubscribeRef.current = subscription.unsubscribe;

      return subscription.unsubscribe;
    },
    [wifiSubscription$]
  );

  return onWifiEvents;
};
